import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import { showToast } from '../lib/toast';

// Google Client ID from environment
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

export default function Login() {
  const { loginWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [googleReady, setGoogleReady] = useState(false);

  // Handle Google Sign-In callback
  const handleGoogleCallback = useCallback(async (response) => {
    setIsLoading(true);
    setError(null);

    try {
      await loginWithGoogle(response.credential);
    } catch (err) {
      const errorMsg = err.message || 'Failed to sign in with Google';
      setError(errorMsg);
      showToast.error(errorMsg);
      setIsLoading(false);
    }
  }, [loginWithGoogle]);

  // Initialize Google Identity Services
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setError('Google Sign-In is not configured. Please contact your administrator.');
      return;
    }

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // Initialize Google Sign-In
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCallback,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Render the Google Sign-In button
      window.google.accounts.id.renderButton(
        document.getElementById('google-signin-button'),
        {
          theme: localStorage.getItem('theme') === 'light' ? 'filled_blue' : 'filled_black',
          size: 'large',
          width: 320,
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
        }
      );

      setGoogleReady(true);
    };

    document.body.appendChild(script);

    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [handleGoogleCallback]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-900 p-4 transition-colors duration-300">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-brand-500/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-to-tr from-brand-600/5 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <div className="card p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img
              src="/logo-icon.svg"
              alt="EdFrame"
              className="w-16 h-16 mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold mb-2 text-slate-900 dark:text-white">EdFrame</h1>
            <p className="text-slate-500 dark:text-slate-400">Privacy-first analytics</p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Google Sign-In Button */}
          <div className="flex flex-col items-center">
            {isLoading ? (
              <div className="w-full py-3 bg-surface-100 dark:bg-surface-700 rounded-lg text-center text-slate-500 dark:text-slate-400">
                Signing in...
              </div>
            ) : (
              <div
                id="google-signin-button"
                className="flex justify-center"
                style={{ minHeight: '44px' }}
              />
            )}
          </div>

          {/* Help text */}
          <p className="mt-6 text-center text-sm text-slate-500">
            Contact your administrator if you need access.
          </p>
        </div>
      </div>
    </div>
  );
}
